import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    input,
    inject,
    output,
} from '@angular/core';
import moment from 'moment';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { Observable, shareReplay, Subject } from 'rxjs';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';

const sharedStylesheet = new CSSStyleSheet();
sharedStylesheet.replaceSync(
    `:host {--position: fixed; --position-bottom: 0; --height: initial; --padding-start: 0; --padding-end: 0; --padding-top: 0; --padding-bottom: 0; --background: initial; --box-shadow: initial; --border-radius: initial; --border-width: initial; --border-style: initial; --border-color: initial; --color: inherit; --font-size: inherit; --line-height: inherit; --font-weight: inherit; --background-hover: transparent; --background-active: var(--background); --background-disabled: transparent; --color-hover: var(--color); --color-active: var(--color); --color-disabled: var(--color); --item-height: initial; --item-padding-start: 0; --item-padding-end: 0; --item-padding-top: 0; --item-padding-bottom: 0; --item-active-padding-start: var(--item-padding-start); --item-active-padding-end: var(--item-padding-end); --item-active-padding-top: var(--item-padding-top); --item-active-padding-bottom: var(--item-padding-bottom); --hightlight-border-radius: initial; --item-muted-background: transparent; } .bazis-timepicker { position: var(--position); z-index: 10; bottom: var(--position-bottom); left: 0; right: 0; width: calc(100% - var(--padding-start) * 2 - var(--padding-end) * 2); background: var(--background); margin: auto; padding-left: var(--padding-start); padding-right: var(--padding-end); padding-top: var(--padding-top); padding-bottom: var(--padding-bottom); border-radius: var(--border-radius); border-width: var(--border-width); border-style: var(--border-style); border-color: var(--border-color); box-shadow: var(--box-shadow); font-size: var(--font-size); line-height: var(--line-height); font-weight: var(--font-weight); color: var(--color); } .bazis-timepicker__content { display: flex; position: relative; width: 100%; } .bazis-timepicker__highlight { background-color: var(--background-active); position: absolute; top: 50%; left: 0; right: 0; transform: translateY(-50%); width: 100%; height: calc(var(--item-height) + var(--item-padding-top) + var(--item-padding-bottom)); z-index: 1; border-radius: var(--hightlight-border-radius); } .bazis-timepicker__separator { display: inline-flex; align-self: center; justify-content: center; height: 100%; z-index: 11; color: var(--color-active); } .bazis-timepicker__list { position: absolute; z-index: 10; outline: 0; bottom: 0; right: 0; left: 0; top: 0; } .bazis-timepicker__list-wrapper { display: inline-block; overflow: hidden; box-sizing: content-box; overflow-x: hidden; overflow-y: auto; position: relative; text-align: center; width: 50%; height: var(--height); } .bazis-timepicker__list-wrapper::before, .bazis-timepicker__list-wrapper::after { display: block; content: " "; position: absolute; z-index: 11; width: 100%; left: 0; pointer-events: none; height: calc(var(--item-height) + var(--item-padding-top) + var(--item-padding-bottom)); box-sizing: border-box; background-color: var(--item-muted-background); } .bazis-timepicker__list-wrapper::after { bottom: 0; } .bazis-time-container { background: transparent; position: absolute; display: block; overflow-x: hidden; overflow-y: scroll; left: 0; right: 0; top: 0; bottom: 0; padding-right: 100px; padding-left: 100px; margin-left: -100px; margin-right: -100px; } .bazis-time-container ul { transform: translateY(calc((var(--item-height) + var(--item-padding-top) + var(--item-padding-bottom)) * 2)); height: auto; margin: 0; padding: 0; border-width: 0; outline: 0; text-decoration: none; list-style: none; } .bazis-time-container ul::after { display: block; content: " "; position: absolute; width: 100%; left: 0; pointer-events: none; height: calc((var(--item-height) + var(--item-padding-top) + var(--item-padding-bottom)) * 2); box-sizing: border-box; } .bazis-time-container ul .bazis-item { display: list-item; padding: var(--item-padding-top) var(--item-padding-end) var(--item-padding-bottom) var(--item-padding-start); min-height: var(--item-height); line-height: var(--item-height); outline: none; cursor: pointer; } .bazis-time-container ul .bazis-item:hover, .bazis-time-container ul .bazis-item:focus { background: var(--background-hover); color: var(--color-hover); } .bazis-time-container ul .bazis-item--active { padding: var(--item-active-padding-top) var(--item-active-padding-end) var(--item-active-padding-bottom) var(--item-active-padding-start); color: var(--color-active); } .bazis-time-container ul .bazis-item--active:hover, .bazis-time-container ul .bazis-item--active:focus { color: var(--color-active); } .bazis-time-container ul .bazis-item--disabled { background: var(--background-disabled); color: var(--color-disabled); }`,
);

@Component({
    selector: 'bazis-timepicker',
    templateUrl: './timepicker.component.html',
    // styleUrls: ['timepicker.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TimepickerElement implements OnInit, AfterViewInit {
    public el = inject(ElementRef);

    readonly value = input<Date>(new Date());

    readonly maxTime = input();

    readonly minTime = input();

    valueChange = output<any>();

    @ViewChild('hContainer') hhContainerRef: ElementRef;

    @ViewChild('mContainer') mmContainerRef: ElementRef;

    @ViewChild('hList') hhListRef: ElementRef;

    @ViewChild('mList') mmListRef: ElementRef;

    scroll$ = new Subject<{ type: 'mm' | 'hh'; value: string }>();

    scrollHH$: Observable<{ type: 'mm' | 'hh'; value: string }>;

    scrollMM$: Observable<{ type: 'mm' | 'hh'; value: string }>;

    hhValue = '00';

    mmValue = '00';

    hhArray = [];

    mmArray = [];

    @HostListener('scroll', ['$event'])
    handleHHScroll(event) {
        // console.log('handleHHScroll', event.target.scrollTop);
        this.hhValue = this.getValueByPosition(event.target.scrollTop, 'hh');
        this.emitUpdateValue();
        this.scroll$.next({ value: this.hhValue, type: 'hh' });
    }

    @HostListener('scroll', ['$event'])
    handleMMScroll(event) {
        // console.log('handleMMScroll', event.target.scrollTop);
        this.mmValue = this.getValueByPosition(event.target.scrollTop, 'mm');
        this.emitUpdateValue();
        this.scroll$.next({ value: this.mmValue, type: 'mm' });
    }

    // DO NOT FILL - auto calculate
    styleSettings = { hh: null, mm: null };

    ngOnInit(): void {
        for (let i = 0; i < 10; i++) {
            this.hhArray.push('0' + i);
        }
        for (let i = 10; i < 24; i++) {
            this.hhArray.push(i.toString());
        }
        for (let i = 0; i < 10; i++) {
            this.mmArray.push('0' + i);
        }
        for (let i = 10; i < 60; i++) {
            this.mmArray.push(i.toString());
        }

        this.scrollHH$ = this.generateScrollObservable$('hh');

        this.scrollMM$ = this.generateScrollObservable$('mm');

        const hh = moment(this.value()).hour();
        const mm = moment(this.value()).minute();

        this.hhValue = moment(this.value()).hour() < 10 ? `0${hh}` : hh.toString();
        this.mmValue = moment(this.value()).minute() < 10 ? `0${mm}` : mm.toString();

        const host = this.el.nativeElement;
        if (host && host.shadowRoot) {
            const shadowRoot = host.shadowRoot;
            const styles = shadowRoot.querySelectorAll('style');
            styles.forEach((style) => style.remove());
            shadowRoot.adoptedStyleSheets = [sharedStylesheet];
        }
    }

    generateScrollObservable$(type) {
        return this.scroll$.asObservable().pipe(
            filter((scroll) => scroll.type === type),
            debounceTime(200),
            tap((scroll) => this.processScroll(scroll)),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    processScroll(scroll) {
        this.calculateSettings(scroll.type);

        const top =
            this.getArrByType(scroll.type).indexOf(scroll.value) *
            this.styleSettings[scroll.type].itemHeight;

        if (top === this.styleSettings[scroll.type].containerElement.scrollTop) return;

        this.styleSettings[scroll.type].containerElement.scrollTo({
            top,
            behavior: 'smooth',
        });
    }

    ngAfterViewInit() {
        this.scrollTo(null, this.hhArray.indexOf(this.hhValue), 'hh', 'instant');
        this.scrollTo(null, this.mmArray.indexOf(this.mmValue), 'mm', 'instant');
    }

    getArrByType(type) {
        return type === 'hh' ? this.hhArray : this.mmArray;
    }

    calculateSettings(type, forceRecalculation = false) {
        if (this.styleSettings[type] && !forceRecalculation) return;
        const listEl = type === 'hh' ? this.hhListRef : this.mmListRef;
        const arr = type === 'hh' ? this.hhArray : this.mmArray;
        const listHeight = listEl.nativeElement.offsetHeight;
        const activeItemEl = listEl.nativeElement.querySelector('.bazis-item--active');
        const activeItemHeight = activeItemEl.offsetHeight;
        const itemEl = listEl.nativeElement.querySelector('.bazis-item:not(.bazis-item--active)');
        this.styleSettings[type] = {
            listHeight,
            itemHeight: (listHeight - activeItemHeight) / (arr.length - 1),
            revertItemHeight: 1 / itemEl.offsetHeight,
            containerElement:
                type === 'hh'
                    ? this.hhContainerRef.nativeElement
                    : this.mmContainerRef.nativeElement,
        };
        // console.log('styleSettings:', this.styleSettings[type]);
    }

    emitUpdateValue() {
        this.valueChange.emit(`${this.hhValue}:${this.mmValue}`);
    }

    getValueByPosition(y, type) {
        this.calculateSettings(type);
        const arr = type === 'hh' ? this.hhArray : this.mmArray;
        const index = Math.round(y * this.styleSettings[type].revertItemHeight);
        // console.log('index:', index, 'pos:', y);
        return arr[index];
    }

    scrollTo(event, index, type, behavior = 'smooth') {
        this.calculateSettings(type);
        // console.log(index, type, { ...this.styleSettings[type] });

        const containerRef = type === 'hh' ? this.hhContainerRef : this.mmContainerRef;
        containerRef.nativeElement.scrollTo({
            top: index * this.styleSettings[type].itemHeight,
            behavior,
        });
    }
}
